import React, { useState, useEffect, useCallback } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import config from '../../config/apiConfig';
import InsumoFormModal from './InsumoFormModal';
import imgDefault from '../../imgs/defecto.png';
import { obtenerInsumos, eliminarInsumo, ajusteRapidoStockInsumo } from '../../services/insumosServicio';
import { obtenerProveedores, guardarProveedor, eliminarProveedor } from '../../services/comprasServicio';

const UNIDAD_LABEL = { UN: 'un', KG: 'kg', G: 'g', L: 'l', ML: 'ml' };

const EMPTY_PROVEEDOR = { nombre: '', rut: '', contacto_nombre: '', telefono: '', email: '', direccion: '' };

// --- MODAL PROVEEDOR ---
const ProviderFormModal = ({ isVisible, onClose, onSave, providerToEdit }) => {
    const [provider, setProvider] = useState(EMPTY_PROVEEDOR);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (isVisible) setProvider(providerToEdit || EMPTY_PROVEEDOR);
    }, [providerToEdit, isVisible]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!provider.nombre) return toast.warning("Debes asignar un nombre");
        setIsLoading(true);
        try {
            await guardarProveedor(provider);
            onSave();
        } catch (err) {
            toast.error("Error al procesar el proveedor");
        } finally {
            setIsLoading(false);
        }
    };

    if (!isVisible) return null;

    return (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content border-0 shadow rounded overflow-hidden">
                    <div className="modal-header bg-white border-0 pt-4 px-4">
                        <h5 className="font-weight-bold m-0">{providerToEdit ? 'Editar Proveedor' : 'Nuevo Proveedor'}</h5>
                        <button className="close" onClick={onClose}>&times;</button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body p-4">
                            <div className="row">
                                <div className="col-md-6 form-group mb-3">
                                    <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block">Nombre / Razón Social</label>
                                    <input type="text" className="form-control border-light bg-light"
                                        value={provider.nombre} onChange={(e) => setProvider({ ...provider, nombre: e.target.value })}
                                        placeholder="Ej. Distribuidora Los Andes" />
                                </div>
                                <div className="col-md-6 form-group mb-3">
                                    <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block">RUT</label>
                                    <input type="text" className="form-control border-light bg-light"
                                        value={provider.rut || ''} onChange={(e) => setProvider({ ...provider, rut: e.target.value })}
                                        placeholder="Ej. 76.123.456-7" />
                                </div>
                                <div className="col-md-6 form-group mb-3">
                                    <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block">Contacto</label>
                                    <input type="text" className="form-control border-light bg-light"
                                        value={provider.contacto_nombre || ''} onChange={(e) => setProvider({ ...provider, contacto_nombre: e.target.value })}
                                        placeholder="Nombre de contacto" />
                                </div>
                                <div className="col-md-6 form-group mb-3">
                                    <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block">Teléfono</label>
                                    <input type="text" className="form-control border-light bg-light"
                                        value={provider.telefono || ''} onChange={(e) => setProvider({ ...provider, telefono: e.target.value })} />
                                </div>
                                <div className="col-md-6 form-group mb-3">
                                    <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block">Email</label>
                                    <input type="email" className="form-control border-light bg-light"
                                        value={provider.email || ''} onChange={(e) => setProvider({ ...provider, email: e.target.value })} />
                                </div>
                                <div className="col-md-6 form-group mb-3">
                                    <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block">Dirección</label>
                                    <input type="text" className="form-control border-light bg-light"
                                        value={provider.direccion || ''} onChange={(e) => setProvider({ ...provider, direccion: e.target.value })} />
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer border-0 bg-white p-4">
                            <button type="button" className="btn btn-link text-muted" onClick={onClose}>Cancelar</button>
                            <button type="submit" className="btn btn-primary rounded-pill px-5 shadow-sm" disabled={isLoading}>
                                {isLoading ? 'Guardando...' : 'Guardar Proveedor'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

// =============================================================================

const GestionInventario = () => {
    // --- PESTAÑA ACTIVA ---
    const [activeTab, setActiveTab] = useState('STOCK');

    // --- ESTADOS: STOCK ---
    const [productos, setProductos] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [busqueda, setBusqueda] = useState("");
    const [filtroCritico, setFiltroCritico] = useState(false);
    const [mostrarTodos, setMostrarTodos] = useState(false);

    // --- ESTADOS: MOVIMIENTOS ---
    const [carrito, setCarrito] = useState([]);
    const [tipoMovimiento, setTipoMovimiento] = useState('COMPRA');
    const [notaMovimiento, setNotaMovimiento] = useState('');
    const [busquedaCarrito, setBusquedaCarrito] = useState("");

    // --- ESTADOS: HISTORIAL ---
    const [historial, setHistorial] = useState([]);
    const [filtroProductoKardex, setFiltroProductoKardex] = useState("");

    // --- ESTADOS: INSUMOS ---
    const [insumos, setInsumos] = useState([]);
    const [isLoadingInsumos, setIsLoadingInsumos] = useState(false);
    const [busquedaInsumos, setBusquedaInsumos] = useState("");
    const [modalInsumoVisible, setModalInsumoVisible] = useState(false);
    const [insumoToEdit, setInsumoToEdit] = useState(null);

    // --- ESTADOS: PROVEEDORES ---
    const [proveedores, setProveedores] = useState([]);
    const [isLoadingProveedores, setIsLoadingProveedores] = useState(false);
    const [busquedaProveedores, setBusquedaProveedores] = useState("");
    const [modalProveedorVisible, setModalProveedorVisible] = useState(false);
    const [proveedorToEdit, setProveedorToEdit] = useState(null);

    // --- API: STOCK ---
    const cargarProductos = async () => {
        setIsLoading(true);
        try {
            const res = await fetch(`${config.api_url}/api/inventario`, {
                headers: { token: localStorage.getItem("token") }
            });
            setProductos(await res.json());
        } catch {
            toast.error("Error al cargar inventario");
        } finally {
            setIsLoading(false);
        }
    };

    const cargarHistorial = async () => {
        try {
            const res = await fetch(`${config.api_url}/api/inventario/historial`, {
                headers: { token: localStorage.getItem("token") }
            });
            setHistorial(await res.json());
        } catch {
            console.error("Error al cargar historial");
        }
    };

    // --- API: INSUMOS ---
    const cargarInsumos = useCallback(async () => {
        setIsLoadingInsumos(true);
        try {
            setInsumos(await obtenerInsumos() || []);
        } catch {
            toast.error("Error al cargar insumos");
        } finally {
            setIsLoadingInsumos(false);
        }
    }, []);

    // --- API: PROVEEDORES ---
    const cargarProveedores = useCallback(async () => {
        setIsLoadingProveedores(true);
        try {
            setProveedores(await obtenerProveedores() || []);
        } catch {
            toast.error("Error al cargar proveedores");
        } finally {
            setIsLoadingProveedores(false);
        }
    }, []);

    useEffect(() => { cargarProductos(); cargarHistorial(); }, []);

    // Carga lazy: solo cuando se abre la pestaña por primera vez
    useEffect(() => {
        if (activeTab === 'INSUMOS' && insumos.length === 0) cargarInsumos();
        if (activeTab === 'PROVEEDORES' && proveedores.length === 0) cargarProveedores();
    }, [activeTab, insumos.length, proveedores.length, cargarInsumos, cargarProveedores]);

    // --- LÓGICA: CARRITO DE MOVIMIENTOS ---
    const agregarAlCarrito = (producto) => {
        if (carrito.find(item => item.producto_id === producto.id)) {
            toast.info("El item ya está en la lista", { autoClose: 1500 });
            return;
        }
        setCarrito([...carrito, {
            producto_id: producto.id,
            tipo: producto.tipo || 'producto',
            nombre: producto.nombre,
            stock_actual: producto.stock,
            cantidad: 1
        }]);
        setBusquedaCarrito("");
    };

    const procesarMovimiento = async (items, tipo, nota, esRapido = false) => {
        if (items.length === 0) return toast.warning("No hay items seleccionados");
        try {
            const res = await fetch(`${config.api_url}/api/inventario/movimiento_masivo`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', token: localStorage.getItem("token") },
                body: JSON.stringify({
                    tipo_movimiento: tipo,
                    nota_general: nota,
                    items: items.map(i => ({ producto_id: i.producto_id, tipo: i.tipo || 'producto', cantidad: i.cantidad }))
                })
            });
            if (res.ok) {
                toast.success("Inventario actualizado");
                if (!esRapido) { setCarrito([]); setNotaMovimiento(''); setActiveTab('STOCK'); }
                cargarProductos();
                cargarHistorial();
            } else {
                const err = await res.json();
                toast.error(err.error || "Error al procesar");
            }
        } catch {
            toast.error("Error de conexión");
        }
    };

    const handleAjusteRapido = (producto, cantidad_ajuste) => {
        const tipo = cantidad_ajuste > 0 ? 'COMPRA' : 'MERMA';
        procesarMovimiento(
            [{ producto_id: producto.id, tipo: producto.tipo || 'producto', cantidad: Math.abs(cantidad_ajuste) }],
            tipo, "Ajuste rápido desde tabla", true
        );
    };

    // --- LÓGICA: INSUMOS ---
    const handleAjusteStockInsumo = async (insumo, delta) => {
        if (!insumo.controlar_stock) return;
        const nuevoStock = parseFloat(insumo.stock || 0) + delta;
        let motivo = '';
        if (delta < 0) {
            motivo = window.prompt(`¿Por qué baja el stock de "${insumo.nombre}"?`, '');
            if (motivo === null) return;
        }
        setInsumos(prev => prev.map(p => p.id === insumo.id ? { ...p, stock: nuevoStock } : p));
        try {
            await ajusteRapidoStockInsumo(insumo.id, nuevoStock, motivo);
        } catch {
            toast.error("Error al ajustar stock");
            cargarInsumos();
        }
    };

    const handleDeleteInsumo = async (insumo) => {
        if (!window.confirm(`¿Eliminar el insumo "${insumo.nombre}"?`)) return;
        try {
            await eliminarInsumo(insumo.id);
            toast.success("Insumo eliminado");
            cargarInsumos();
            cargarProductos();
        } catch {
            toast.error("No se pudo eliminar el insumo");
        }
    };

    // --- LÓGICA: PROVEEDORES ---
    const handleDeleteProveedor = async (id, nombre) => {
        if (!window.confirm(`¿Eliminar el proveedor "${nombre}"?`)) return;
        try {
            await eliminarProveedor(id);
            toast.success("Proveedor eliminado");
            cargarProveedores();
        } catch {
            toast.error("No se pudo eliminar");
        }
    };

    const handleSaveProveedor = () => {
        setModalProveedorVisible(false);
        setProveedorToEdit(null);
        toast.success("¡Proveedor guardado!");
        cargarProveedores();
    };

    // --- UTILS ---
    const formatMoney = (v) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(v);
    const formatearFecha = (f) => new Date(f).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' });
    const formatCantidad = (v) => (v === null || v === undefined) ? 0 : parseFloat(v);

    const getSemaforoClass = (p) => {
        if (!p.controlar_stock) return 'badge-secondary';
        const s = parseFloat(p.stock || 0), m = parseFloat(p.stock_minimo || 0);
        if (s <= 0) return 'badge-danger';
        if (s <= m) return 'badge-warning';
        return 'badge-success';
    };

    // --- FILTROS ---
    const productosFiltrados = productos.filter(p => {
        const txt = (p.nombre + ' ' + (p.codigo || '')).toLowerCase();
        const q = busqueda.toLowerCase();
        const coincide = txt.includes(q);
        if (filtroCritico) return coincide && p.controlar_stock && parseFloat(p.stock) <= parseFloat(p.stock_minimo);
        if (!mostrarTodos) return coincide && p.controlar_stock;
        return coincide;
    });

    const productosBuscadorCarrito = busquedaCarrito
        ? productos.filter(p =>
            p.controlar_stock &&
            (p.nombre + ' ' + (p.codigo || '')).toLowerCase().includes(busquedaCarrito.toLowerCase())
          )
        : [];

    const historialFiltrado = filtroProductoKardex
        ? historial.filter(h => h.item_nombre === filtroProductoKardex)
        : historial;

    const nombresKardex = [...new Set(historial.map(h => h.item_nombre))].sort((a, b) => a.localeCompare(b));

    const insumosFiltrados = insumos.filter(p =>
        (p.nombre || '').toLowerCase().includes(busquedaInsumos.toLowerCase())
    );

    const proveedoresFiltrados = proveedores.filter(p =>
        (p.nombre || '').toLowerCase().includes(busquedaProveedores.toLowerCase())
    );

    // ==========================================================================
    return (
        <div className="bg-light min-vh-100 py-4 px-3">
            <ToastContainer position="bottom-right" />

            <div className="container-fluid" style={{ maxWidth: '1200px' }}>
                <h2 className="font-weight-bold text-dark mb-1">Inventario</h2>
                <p className="text-muted mb-4">Stock, movimientos, insumos y proveedores.</p>

                {/* TABS */}
                <ul className="nav nav-pills bg-white p-2 rounded shadow-sm mb-4" style={{ flexWrap: 'wrap', gap: '4px' }}>
                    <li className="nav-item">
                        <button className={`nav-link font-weight-bold ${activeTab === 'STOCK' ? 'active' : ''}`} onClick={() => setActiveTab('STOCK')}>
                            <i className="fas fa-box mr-2"></i> Stock Actual
                        </button>
                    </li>
                    <li className="nav-item">
                        <button className={`nav-link font-weight-bold ${activeTab === 'MOVIMIENTO' ? 'active bg-success' : 'text-success'}`} onClick={() => setActiveTab('MOVIMIENTO')}>
                            <i className="fas fa-exchange-alt mr-2"></i> Ingresos y Mermas
                        </button>
                    </li>
                    <li className="nav-item">
                        <button className={`nav-link font-weight-bold ${activeTab === 'HISTORIAL' ? 'active bg-secondary' : 'text-secondary'}`} onClick={() => setActiveTab('HISTORIAL')}>
                            <i className="fas fa-history mr-2"></i> Historial (Kardex)
                        </button>
                    </li>
                    <li className="nav-item">
                        <button className={`nav-link font-weight-bold ${activeTab === 'INSUMOS' ? 'active' : 'text-warning'}`}
                            style={activeTab === 'INSUMOS' ? { backgroundColor: '#f59e0b', color: '#fff' } : {}}
                            onClick={() => setActiveTab('INSUMOS')}>
                            <i className="fas fa-carrot mr-2"></i> Insumos
                        </button>
                    </li>
                    <li className="nav-item">
                        <button className={`nav-link font-weight-bold ${activeTab === 'PROVEEDORES' ? 'active bg-info' : 'text-info'}`} onClick={() => setActiveTab('PROVEEDORES')}>
                            <i className="fas fa-handshake mr-2"></i> Proveedores
                        </button>
                    </li>
                </ul>

                {/* ===================== STOCK ===================== */}
                {activeTab === 'STOCK' && (
                    <div className="card border-0 shadow-sm rounded-lg">
                        <div className="card-body p-3 d-flex justify-content-between align-items-center border-bottom">
                            <div className="input-group w-50">
                                <div className="input-group-prepend">
                                    <span className="input-group-text bg-white"><i className="fas fa-search text-muted"></i></span>
                                </div>
                                <input type="text" className="form-control border-left-0" placeholder="Buscar por nombre o código..."
                                    value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
                            </div>
                            <div className="d-flex align-items-center">
                                <div className="custom-control custom-switch mr-4">
                                    <input type="checkbox" className="custom-control-input" id="mostrarTodosSwitch" checked={mostrarTodos} onChange={(e) => setMostrarTodos(e.target.checked)} />
                                    <label className="custom-control-label text-muted font-weight-bold" htmlFor="mostrarTodosSwitch">Mostrar todos</label>
                                </div>
                                <div className="custom-control custom-switch">
                                    <input type="checkbox" className="custom-control-input" id="criticoSwitch" checked={filtroCritico} onChange={(e) => setFiltroCritico(e.target.checked)} />
                                    <label className="custom-control-label text-danger font-weight-bold" htmlFor="criticoSwitch">Solo Crítico</label>
                                </div>
                            </div>
                        </div>
                        <div className="table-responsive" style={{ maxHeight: '65vh' }}>
                            <table className="table table-hover align-middle mb-0">
                                <thead className="bg-light sticky-top">
                                    <tr>
                                        <th>Item</th>
                                        <th className="text-center">Costo</th>
                                        <th className="text-center">Stock Actual</th>
                                        <th className="text-right">Ajuste Rápido</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? <tr><td colSpan="4" className="text-center py-4">Cargando...</td></tr> :
                                        productosFiltrados.map((p) => (
                                            <tr key={(p.tipo || 'producto') + '-' + p.id}>
                                                <td>
                                                    <strong>{p.nombre}</strong>
                                                    {' '}
                                                    <span className={'badge badge-sm ' + (p.tipo === 'insumo' ? 'badge-info' : 'badge-secondary')} style={{ fontSize: '10px', verticalAlign: 'middle' }}>
                                                        {p.tipo === 'insumo' ? 'Insumo' : 'Producto'}
                                                    </span>
                                                    <br />
                                                    <small className="text-muted">{p.codigo}</small>
                                                </td>
                                                <td className="text-center">{p.controlar_stock ? formatMoney(p.costo) : '-'}</td>
                                                <td className="text-center">
                                                    {p.controlar_stock ? (
                                                        <span className={`badge px-3 py-2 ${parseFloat(p.stock) <= parseFloat(p.stock_minimo) ? 'badge-danger' : 'badge-success'}`}>
                                                            {formatCantidad(p.stock)} {p.unidad_medida || 'un'}.
                                                        </span>
                                                    ) : <span className="badge badge-secondary">Ilimitado</span>}
                                                </td>
                                                <td className="text-right">
                                                    {p.controlar_stock && (
                                                        <div className="btn-group shadow-sm">
                                                            <button className="btn btn-sm btn-light text-danger border" onClick={() => handleAjusteRapido(p, -1)}>-1</button>
                                                            <button className="btn btn-sm btn-light text-success border" onClick={() => handleAjusteRapido(p, 1)}>+1</button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* ===================== MOVIMIENTOS ===================== */}
                {activeTab === 'MOVIMIENTO' && (
                    <div className="row">
                        <div className="col-md-5 mb-3">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-header bg-white font-weight-bold">1. Buscar Items</div>
                                <div className="card-body p-2">
                                    <div className="input-group mb-2">
                                        <div className="input-group-prepend">
                                            <span className="input-group-text bg-white"><i className="fas fa-search text-muted"></i></span>
                                        </div>
                                        <input type="text" className="form-control border-left-0"
                                            placeholder="Buscar producto o insumo..."
                                            value={busquedaCarrito} onChange={e => setBusquedaCarrito(e.target.value)} />
                                        {busquedaCarrito && (
                                            <div className="input-group-append">
                                                <button className="btn btn-outline-secondary bg-white border-left-0" onClick={() => setBusquedaCarrito('')}>
                                                    <i className="fas fa-times"></i>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <div className="list-group custom-scrollbar" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                                        {productosBuscadorCarrito.length === 0 ? (
                                            <div className="text-center p-3 text-muted">{busquedaCarrito ? 'Sin resultados' : 'Escribe para buscar...'}</div>
                                        ) : (
                                            productosBuscadorCarrito.map(p => (
                                                <button key={(p.tipo || 'producto') + '-' + p.id}
                                                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center p-2"
                                                    onClick={() => agregarAlCarrito(p)}>
                                                    <div>
                                                        <span className="d-block font-weight-bold" style={{ fontSize: '0.9rem' }}>{p.nombre}</span>
                                                        <small className="text-muted">
                                                            Stock: {formatCantidad(p.stock)} {UNIDAD_LABEL[p.unidad_medida] || 'un'}
                                                            {' · '}
                                                            <span className={'badge badge-sm ' + (p.tipo === 'insumo' ? 'badge-info' : 'badge-secondary')} style={{ fontSize: '9px' }}>
                                                                {p.tipo === 'insumo' ? 'Insumo' : 'Producto'}
                                                            </span>
                                                        </small>
                                                    </div>
                                                    <i className="fas fa-plus-circle text-primary"></i>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-md-7 mb-3">
                            <div className="card border-0 shadow-sm h-100" style={{ borderTop: '4px solid #28a745' }}>
                                <div className="card-header bg-white font-weight-bold">2. Detalle del Movimiento</div>
                                <div className="card-body d-flex flex-column">
                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            <label className="small text-muted font-weight-bold">Tipo de Registro</label>
                                            <select className="form-control font-weight-bold" value={tipoMovimiento} onChange={e => setTipoMovimiento(e.target.value)}>
                                                <option value="COMPRA">🟩 Ingreso (Compra/Recepción)</option>
                                                <option value="MERMA">🟥 Salida (Merma/Vencido)</option>
                                                <option value="DEVOLUCION">🟨 Salida (Devolución a proveedor)</option>
                                                <option value="AJUSTE_MANUAL">🟦 Ajuste Cuadratura</option>
                                            </select>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="small text-muted font-weight-bold">Nota / Doc. Referencia</label>
                                            <input type="text" className="form-control" placeholder="Ej: Factura 1234"
                                                value={notaMovimiento} onChange={e => setNotaMovimiento(e.target.value)} />
                                        </div>
                                    </div>

                                    <div className="table-responsive flex-grow-1 border rounded mb-3">
                                        <table className="table table-sm mb-0">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th>Item</th>
                                                    <th className="text-center" style={{ width: '120px' }}>Cantidad</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {carrito.length === 0 ?
                                                    <tr><td colSpan="3" className="text-center text-muted py-4">No has agregado items a la lista</td></tr> :
                                                    carrito.map(item => (
                                                        <tr key={item.producto_id}>
                                                            <td className="align-middle">
                                                                <strong>{item.nombre}</strong><br />
                                                                <small className="text-muted">Stock actual: {formatCantidad(item.stock_actual)}</small>
                                                            </td>
                                                            <td className="align-middle">
                                                                <input type="number" className="form-control form-control-sm text-center"
                                                                    min="0" step="0.01" value={item.cantidad}
                                                                    onChange={(e) => setCarrito(carrito.map(i => i.producto_id === item.producto_id ? { ...i, cantidad: parseFloat(e.target.value) || 0 } : i))} />
                                                            </td>
                                                            <td className="align-middle text-right">
                                                                <button className="btn btn-sm btn-outline-danger border-0" onClick={() => setCarrito(carrito.filter(i => i.producto_id !== item.producto_id))}>
                                                                    <i className="fas fa-trash"></i>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    <button className="btn btn-success btn-lg font-weight-bold shadow-sm"
                                        disabled={carrito.length === 0}
                                        onClick={() => procesarMovimiento(carrito, tipoMovimiento, notaMovimiento)}>
                                        <i className="fas fa-save mr-2"></i> Procesar {tipoMovimiento} ({carrito.length} items)
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ===================== HISTORIAL (KARDEX) ===================== */}
                {activeTab === 'HISTORIAL' && (
                    <div className="card border-0 shadow-sm rounded-lg">
                        <div className="card-body p-3 border-bottom bg-light">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <div className="input-group shadow-sm">
                                        <div className="input-group-prepend">
                                            <span className="input-group-text bg-white border-right-0"><i className="fas fa-filter text-primary"></i></span>
                                        </div>
                                        <select className="form-control border-left-0 font-weight-bold"
                                            value={filtroProductoKardex} onChange={(e) => setFiltroProductoKardex(e.target.value)}>
                                            <option value="">-- Ver Todos --</option>
                                            {nombresKardex.map(nombre => (
                                                <option key={nombre} value={nombre}>{nombre}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="col-md-6 text-right text-muted small">
                                    {filtroProductoKardex ? `Historial de: ${filtroProductoKardex}` : 'Últimos movimientos globales'}
                                </div>
                            </div>
                        </div>
                        <div className="table-responsive" style={{ maxHeight: '65vh' }}>
                            <table className="table table-hover table-sm align-middle mb-0">
                                <thead className="bg-white sticky-top shadow-sm">
                                    <tr>
                                        <th className="py-3 pl-3">Fecha</th>
                                        <th className="py-3">Tipo</th>
                                        <th className="py-3">Origen</th>
                                        <th className="py-3">Ítem</th>
                                        <th className="py-3 text-center">Cant.</th>
                                        <th className="py-3 text-center">Stock Final</th>
                                        <th className="py-3">Nota</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historialFiltrado.length === 0 ?
                                        <tr><td colSpan="7" className="text-center py-5 text-muted">No hay movimientos</td></tr> :
                                        historialFiltrado.map((h) => {
                                            let bc = 'badge-secondary';
                                            if (h.tipo === 'COMPRA') bc = 'badge-success';
                                            else if (['MERMA', 'VENTA', 'DEVOLUCION'].includes(h.tipo)) bc = 'badge-danger';
                                            else if (h.tipo === 'AJUSTE_MANUAL') bc = 'badge-primary';
                                            return (
                                                <tr key={`${h.tipo_item}-${h.id}`}>
                                                    <td className="pl-3 text-muted small">{formatearFecha(h.created_at)}</td>
                                                    <td><span className={`badge ${bc}`}>{h.tipo}</span></td>
                                                    <td><span className={`badge ${h.tipo_item === 'INSUMO' ? 'badge-info' : 'badge-light border'}`}>{h.tipo_item === 'INSUMO' ? 'Insumo' : 'Producto'}</span></td>
                                                    <td className="font-weight-bold">{h.item_nombre}</td>
                                                    <td className="text-center font-weight-bold" style={{ color: h.cantidad > 0 ? 'green' : 'red' }}>
                                                        {h.cantidad > 0 ? '+' : ''}{formatCantidad(h.cantidad)} {UNIDAD_LABEL[h.unidad_medida] || ''}
                                                    </td>
                                                    <td className="text-center text-muted">{formatCantidad(h.stock_nuevo)} {UNIDAD_LABEL[h.unidad_medida] || ''}</td>
                                                    <td className="small text-muted">{h.nota || '-'}</td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* ===================== INSUMOS ===================== */}
                {activeTab === 'INSUMOS' && (
                    <div className="card border-0 shadow-sm rounded-lg overflow-hidden">
                        <div className="card-header bg-white d-flex justify-content-between align-items-center p-3">
                            <div className="input-group" style={{ maxWidth: '400px' }}>
                                <div className="input-group-prepend">
                                    <span className="input-group-text bg-white"><i className="fas fa-search text-muted"></i></span>
                                </div>
                                <input type="text" className="form-control border-left-0" placeholder="Buscar insumo..."
                                    value={busquedaInsumos} onChange={(e) => setBusquedaInsumos(e.target.value)} />
                            </div>
                            <button className="btn btn-primary rounded-pill px-4" onClick={() => { setInsumoToEdit(null); setModalInsumoVisible(true); }}>
                                <i className="fas fa-plus mr-2"></i> Nuevo Insumo
                            </button>
                        </div>
                        <div className="table-responsive" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                            <table className="table table-hover align-middle mb-0">
                                <thead className="bg-light" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                                    <tr>
                                        <th className="px-4 py-3 text-muted small text-uppercase">Insumo</th>
                                        <th className="py-3 text-muted small text-uppercase">Costo</th>
                                        <th className="py-3 text-muted small text-uppercase text-center" style={{ width: '200px' }}>Stock</th>
                                        <th className="px-4 py-3 text-right text-muted small text-uppercase">Opciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoadingInsumos ? (
                                        <tr><td colSpan="4" className="text-center py-5">Cargando insumos...</td></tr>
                                    ) : insumosFiltrados.length === 0 ? (
                                        <tr><td colSpan="4" className="text-center py-5 text-muted">No hay insumos registrados.</td></tr>
                                    ) : (
                                        insumosFiltrados.map((p) => (
                                            <tr key={p.id}>
                                                <td className="px-4 py-3">
                                                    <div className="d-flex align-items-center">
                                                        <div className="bg-light rounded mr-3" style={{ width: '40px', height: '40px', overflow: 'hidden', flexShrink: 0 }}>
                                                            <img src={p.url_imagen || p.img || imgDefault} alt=""
                                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                onError={(e) => { e.target.src = imgDefault; }} />
                                                        </div>
                                                        <span className="font-weight-bold text-dark">{p.nombre}</span>
                                                    </div>
                                                </td>
                                                <td className="py-3 text-muted">
                                                    {new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(p.costo || 0)}
                                                </td>
                                                <td className="py-3 text-center">
                                                    {p.controlar_stock ? (
                                                        <div className="d-flex align-items-center justify-content-center">
                                                            <button className="btn btn-sm btn-light border" onClick={() => handleAjusteStockInsumo(p, -1)}>-</button>
                                                            <span className={`badge px-2 py-2 mx-2 rounded-pill ${getSemaforoClass(p)}`} style={{ minWidth: '60px' }}>
                                                                {formatCantidad(p.stock || 0)} {UNIDAD_LABEL[p.unidad_medida] || 'un'}
                                                            </span>
                                                            <button className="btn btn-sm btn-light border" onClick={() => handleAjusteStockInsumo(p, 1)}>+</button>
                                                        </div>
                                                    ) : (
                                                        <span className="badge badge-secondary px-3 py-2 rounded-pill">S/C</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <button className="btn btn-sm btn-light rounded-circle mr-2" onClick={() => { setInsumoToEdit(p); setModalInsumoVisible(true); }}>
                                                        <i className="fas fa-pen text-primary"></i>
                                                    </button>
                                                    <button className="btn btn-sm btn-light rounded-circle" onClick={() => handleDeleteInsumo(p)}>
                                                        <i className="fas fa-trash text-danger"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* ===================== PROVEEDORES ===================== */}
                {activeTab === 'PROVEEDORES' && (
                    <div className="card border-0 shadow-sm rounded-lg overflow-hidden">
                        <div className="card-header bg-white d-flex justify-content-between align-items-center p-3">
                            <div className="input-group" style={{ maxWidth: '400px' }}>
                                <div className="input-group-prepend">
                                    <span className="input-group-text bg-white"><i className="fas fa-search text-muted"></i></span>
                                </div>
                                <input type="text" className="form-control border-left-0" placeholder="Buscar proveedor..."
                                    value={busquedaProveedores} onChange={(e) => setBusquedaProveedores(e.target.value)} />
                            </div>
                            <button className="btn btn-primary rounded-pill px-4" onClick={() => { setProveedorToEdit(null); setModalProveedorVisible(true); }}>
                                <i className="fas fa-plus mr-2"></i> Nuevo Proveedor
                            </button>
                        </div>
                        <div className="table-responsive" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                            <table className="table table-hover align-middle mb-0">
                                <thead className="bg-light sticky-top" style={{ zIndex: 10 }}>
                                    <tr>
                                        <th className="px-4 py-3 text-muted small text-uppercase">Nombre</th>
                                        <th className="py-3 text-muted small text-uppercase">RUT</th>
                                        <th className="py-3 text-muted small text-uppercase">Contacto</th>
                                        <th className="px-4 py-3 text-right text-muted small text-uppercase">Opciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoadingProveedores ? (
                                        <tr><td colSpan="4" className="text-center py-5">Cargando proveedores...</td></tr>
                                    ) : proveedoresFiltrados.length === 0 ? (
                                        <tr><td colSpan="4" className="text-center py-5 text-muted">No hay proveedores registrados.</td></tr>
                                    ) : (
                                        proveedoresFiltrados.map(p => (
                                            <tr key={p.id}>
                                                <td className="px-4 py-3 font-weight-bold text-dark">{p.nombre}</td>
                                                <td className="py-3 text-muted">{p.rut || '-'}</td>
                                                <td className="py-3 text-muted">
                                                    {p.contacto_nombre || '-'}
                                                    {p.telefono ? ` · ${p.telefono}` : ''}
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <button className="btn btn-sm btn-light rounded-circle mr-2" onClick={() => { setProveedorToEdit(p); setModalProveedorVisible(true); }}>
                                                        <i className="fas fa-pen text-primary"></i>
                                                    </button>
                                                    <button className="btn btn-sm btn-light rounded-circle" onClick={() => handleDeleteProveedor(p.id, p.nombre)}>
                                                        <i className="fas fa-trash text-danger"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

            </div>

            <style jsx>{`
                .custom-scrollbar::-webkit-scrollbar { width: 6px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #ccc; border-radius: 10px; }
            `}</style>

            <InsumoFormModal
                isVisible={modalInsumoVisible}
                onClose={() => { setModalInsumoVisible(false); setInsumoToEdit(null); }}
                onSave={() => {
                    setModalInsumoVisible(false);
                    setInsumoToEdit(null);
                    toast.success("¡Insumo guardado!");
                    cargarInsumos();
                    cargarProductos();
                }}
                insumoToEdit={insumoToEdit}
            />

            <ProviderFormModal
                isVisible={modalProveedorVisible}
                onClose={() => { setModalProveedorVisible(false); setProveedorToEdit(null); }}
                onSave={handleSaveProveedor}
                providerToEdit={proveedorToEdit}
            />
        </div>
    );
};

export default GestionInventario;
